import { useEffect, useRef, useState } from "react";
import { renderToString } from "react-dom/server";
import Layout from "../../../../components/layouts/Layout";
import Section from "../../../../components/base/Section";
import { getUserId, isActiveTech, isDarkMode, openJobQuickView, openTechQuickView, setPage, setToolbarActions, setToolbarComponent, useEnsureJobs, useGlobal, useJobs, useScreen, useSocket, useStatuses } from "../../../../core/Global";
import JobsBoardActions from "../components/JobsBoardActions";
import { useRememberJobsBoard } from "../utils/jobsBoardState";
import JobsFilterBar from "../components/JobsFilterBar";
import Job from "../models/Job";

import { HereGeocodePosition } from "../../../../models/MapModels";
import FlexRow from "../../../../components/base/FlexRow";
import Icon from "../../../../components/base/Icon";
import DropdownMenu, { MenuItem } from "../../../../components/menu/DropdownMenu";
import { techMarkerIcon } from "../../../../components/map/TechMarker";
import TechMapPopup from "../../../../components/map/TechMapPopup";
import { jobMatchesKeyword } from "../utils/jobFilters";
import Technician from "../../../technicians/models/technician";
import { applyTechDeviceState, applyTechLive, isTechLive, onTechClock, onTechDeviceState, techClock, techLivePoint, TechMapLocationUpdate, watchTechClocks } from "../../../../core/TechLive";

// Same free OpenStreetMap tiles in both themes; dark mode inverts them with CSS
// (.rinvox-dark-tiles in App.css). CARTO's dark basemap needs an API key now.
const tileUrl = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const DARK_TILES_CLASS = "rinvox-dark-tiles";

const JobTooltip = ({ job }: { job: Job }) => (
    <div style={{ minWidth: 210, padding: "6px 2px", fontFamily: "inherit" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
            <span style={{ fontWeight: 700, fontSize: 12, color: job.jobStatus?.color }}>
                #{job.jobId}
            </span>
            <span style={{ fontSize: 11, color: "#888", background: "#f3f3f3", borderRadius: 6, padding: "1px 6px" }}>
                {job.jobStatus?.name}
            </span>
        </div>
        <div style={{ fontSize: 11, marginBottom: 3 }}>
            <span style={{ color: "#888" }}>Client: </span>{job.fullName ?? "-"}
        </div>
        <div style={{ fontSize: 11, marginBottom: 3 }}>
            <span style={{ color: "#888" }}>Phone: </span>{(job.phone as any)?.phoneFriendly ?? "-"}
        </div>
        <div style={{ fontSize: 11, marginBottom: 3 }}>
            <span style={{ color: "#888" }}>Address: </span>{job.address?.address?.label ?? "-"}
        </div>
        <div style={{ fontSize: 11 }}>
            <span style={{ color: "#888" }}>Tech: </span>{job.tech?.fullName ?? "-"}
        </div>
    </div>
);



export default function JobsMapView() {

    const [globalState] = useGlobal();
    const screen = useScreen();
    const socket = useSocket();

    useEnsureJobs()
    useRememberJobsBoard()

    const mapRef = useRef<any>(null);
    const techMarkersRef = useRef<Record<string, any>>({});

    /** Keeps the hovered panel open for the moment it takes to move the pointer into it. */
    const popupCloseTimer = useRef<number | undefined>(undefined);

    /** The board as it stands, for the hover panel to count jobs out of without the markers
     *  having to be rebuilt every time a job moves. */
    const jobsRef = useRef<Job[]>([]);
    jobsRef.current = globalState.jobs ?? [];

    /**
     * Who is currently wearing the live ring.
     *
     * Kept as a set so going quiet costs nothing to notice: the sweep below walks the handful
     * of technicians that are actually reporting rather than every marker on the map. A
     * technician who has not reported all day is never looked at.
     */
    const liveMarkersRef = useRef<Set<string>>(new Set());

    // The header's applied filters, as one predicate (see utils/jobFilters).
    const [boardFilter, boardFilterSet] = useState<(job: Job) => boolean>();
    const [findKeyword, findKeywordSet] = useState<string | undefined>();

    const MapFilters = () => (
        <JobsFilterBar
            mode="map"
            onKeywordChange={findKeywordSet}
            onFilterChange={f => boardFilterSet(() => f)}
        />
    );

    useEffect(() => {
        setToolbarComponent(MapFilters);
        setToolbarActions(() => <JobsBoardActions />);
        setPage("Map", "CRM", "Jobs");

        return () => {
            setToolbarComponent(undefined);
            setToolbarActions(undefined);
        };
    }, []);

    useEffect(() => {
        const L = window.GetLeafletInstance();

        const m = L.map("jobs-map-view", {
            zoomControl: true,
            attributionControl: false,
            zoom: 4,
            center: [39.8097343, -98.5556199],
            layers: [L.tileLayer(tileUrl, { opacity: 0.7, className: isDarkMode() ? DARK_TILES_CLASS : "" })]
        });

        mapRef.current = m;

        // Hovering the panel itself keeps it open, so it can be read at leisure — nothing in
        // it is clickable, the pin is.
        m.on("popupopen", (e: any) => {
            const el: HTMLElement | null = e.popup?.getElement?.();
            if (!el || el.dataset.wired === "1") return;

            el.dataset.wired = "1";

            el.addEventListener("mouseenter", () => window.clearTimeout(popupCloseTimer.current));
            el.addEventListener("mouseleave", () => {
                popupCloseTimer.current = window.setTimeout(() => m.closePopup(), 200);
            });
        });

        return () => {
            window.clearTimeout(popupCloseTimer.current);
            m.remove();
            mapRef.current = null;
            techMarkersRef.current = {};
        };
    }, []);

    // Job markers — filtered by status, source, tech
    useEffect(() => {
        const m = mapRef.current;
        if (!m) return;

        const L = window.GetLeafletInstance();

        m.eachLayer((lay: any) => {
            if (lay.options?.jobMarker) m.removeLayer(lay);
        });

        let jobs = globalState.jobs ?? [];

        if (boardFilter) jobs = jobs.filter(boardFilter);

        if (findKeyword) jobs = jobs.filter(j => jobMatchesKeyword(j, findKeyword));

        const positions: [number, number][] = [];

        jobs.forEach(job => {
            const pos = job.address?.position;
            if (!pos?.lat || !pos?.lng) return;

            const marker = L.marker([pos.lat, pos.lng], {
                icon: L.icon({
                    iconUrl: `/assets/media/mapmarkers/client.png`,
                    iconSize: [40, 40],
                    iconAnchor: [20, 40],
                }),
                draggable: false,
                jobMarker: true
            });

            marker.bindTooltip(renderToString(<JobTooltip job={job} />), {
                direction: "top",
                offset: [0, -42],
                opacity: 1,
            });

            marker.on("click", () => openJobQuickView(job.id));
            marker.addTo(m);
            positions.push([pos.lat, pos.lng]);
        });

        if (positions.length > 0) {
            m.fitBounds(L.latLngBounds(positions), { padding: [50, 50] });
        }
    }, [globalState.jobs, mapRef.current, boardFilter, findKeyword]);

    /**
     * Puts the live ring on a marker, or takes it off, and keeps the set in step.
     *
     * A class on the marker's element rather than a new icon: `setIcon` throws away the pin's
     * DOM and builds it again, which is a great deal of work to change one colour, and on a
     * map following a whole company it would be happening constantly.
     */
    /**
     * Puts one marker in its current state: green while live (on the clock and reporting),
     * grey while off the clock, plain white in between — on the clock but quiet for a while.
     * Two classes on the element, so a change costs nothing like a rebuilt icon.
     */
    const refreshMarker = (techId: string) => {
        const marker = techMarkersRef.current[techId];
        if (!marker) return;

        const tech: Technician | undefined = marker.options?.tech;
        const reportedAt = techLivePoint(techId)?.at ?? (tech?.techLocation ? tech.techLocationLastUpdate : undefined);

        const live = isTechLive(techId, reportedAt);
        // Not known yet counts as off: the list of who is on the clock arrives a moment after
        // the map, and anyone on it is turned back on as it does.
        const offline = !techClock(techId)?.on;

        if (live) liveMarkersRef.current.add(techId);
        else liveMarkersRef.current.delete(techId);

        const el = marker.getElement?.();
        if (el) {
            el.classList.toggle("tech-marker-live", live);
            el.classList.toggle("tech-marker-offline", offline);
        }
    };

    /**
     * The panel for one technician, built when it is asked for.
     *
     * Not when the marker is made: rendering a React tree for every technician on a map that
     * may hold a thousand of them costs a thousand renders nobody asked to see. Built on open
     * it also reads the current time, so "Live" and "7 minutes ago" are true when looked at
     * rather than when the map loaded.
     */
    const popupFor = (tech: Technician) => {
        const live = techLivePoint(tech.id);

        const shown: Technician = live
            ? { ...tech, techLocation: live.position, techLocationLastUpdate: live.at }
            : tech;

        return renderToString(
            <TechMapPopup
                tech={shown}
                jobs={(jobsRef.current ?? []).filter(job => job.techId === tech.id)}
                isWakeupAddress={!shown.techLocation}
            />
        );
    };

    // Tech markers — the face on the map, with the panel built on hover
    useEffect(() => {
        const m = mapRef.current;
        if (!m) return;

        const L = window.GetLeafletInstance();

        Object.values(techMarkersRef.current).forEach(marker => m.removeLayer(marker));
        techMarkersRef.current = {};

        // Only technicians who can still take work; a suspended or deleted one is not on the map.
        const techs = (globalState.techs ?? []).filter(isActiveTech);

        techs.forEach(tech => {
            // Whatever has come in over the socket this session beats what the board was
            // loaded with — the bank is only refetched now and then.
            const live = techLivePoint(tech.id);
            const pos = live?.position ?? tech.techLocation ?? tech.techWakeupLocation;
            if (!pos?.lat || !pos?.lng) return;

            const marker = L.marker([pos.lat, pos.lng], {
                // Their own face. A dozen identical icons tell you somebody is there but never
                // who, which is the whole question this map is asked.
                icon: techMarkerIcon(L, tech, { size: 40 }),
                draggable: false,
                techMarker: true,
                tech
            });

            // A popup rather than a tooltip, because this one is hovered into and read at
            // leisure; a tooltip disappears the moment the pointer leaves the marker.
            marker.bindPopup("", {
                closeButton: false,
                autoPan: false,
                maxWidth: 320,
                minWidth: 250,
                className: "tech-map-popup-wrap"
            });

            marker.on("mouseover", () => {
                window.clearTimeout(popupCloseTimer.current);
                marker.setPopupContent(popupFor(tech));
                marker.openPopup();
            });

            marker.on("mouseout", () => {
                popupCloseTimer.current = window.setTimeout(() => marker.closePopup(), 260);
            });

            // Everything the small panel left out — the rest of their details and the jobs
            // themselves — opens here, where there is room for it.
            marker.on("click", () => {
                marker.closePopup();
                openTechQuickView(tech.id);
            });

            marker.addTo(m);

            if (tech.id) {
                techMarkersRef.current[tech.id] = marker;

                // Somebody already live when the map opens wears the ring from the start,
                // rather than only once their next point happens to arrive.
                refreshMarker(tech.id);
            }
        });

        // The panel lists a count of their jobs, so a board that changed is a panel that must
        // be rebuilt — but it is rebuilt on hover, so nothing is redrawn here for it.
    }, [globalState.techs, mapRef.current]);

    // Live technician locations.
    //
    // They arrive in batches — the server collects a window's worth and sends one message, so a
    // company with a thousand technicians on the clock is one event every couple of seconds
    // rather than a hundred a second. Applied straight to the Leaflet markers: moving a dot
    // must not re-render the page.
    useEffect(() => {
        const ownerUserId = getUserId();
        if (!ownerUserId) return;

        const apply = (techId: string, position: HereGeocodePosition, at?: Date | string) => {
            if (!techId || !position?.lat || !position?.lng) return;

            applyTechLive(techId, position, at);

            const marker = techMarkersRef.current[techId];
            if (!marker) return;

            marker.setLatLng([position.lat, position.lng]);

            refreshMarker(techId);

            // A marker that just reported says so for a moment on top of the ring: a pulse
            // catches the eye somewhere else on the map, which a steady colour does not. Not
            // for somebody off the clock — a grey face pulsing green says the opposite of grey.
            const el = marker.getElement?.();
            if (el && techClock(techId)?.on) {
                el.classList.remove("tech-marker-reported");
                // Reading it back restarts the animation; without this a second point inside
                // the same second does nothing at all.
                void el.offsetWidth;
                el.classList.add("tech-marker-reported");
            }

            // Only the panel actually being read is rebuilt, and only while it is open.
            if (marker.isPopupOpen?.() && marker.options?.tech) {
                marker.setPopupContent(popupFor(marker.options.tech));
            }
        };

        const key = socket.start(
            skt => {
                skt.on("TechMapLocationsUpdate", (batch: TechMapLocationUpdate[]) => {
                    (batch ?? []).forEach(item => {
                        // The battery came with the point; the panel's battery line reads it from there.
                        if (item.batteryLevel !== undefined && item.batteryLevel !== null) {
                            applyTechDeviceState(item.techId, { batteryLevel: item.batteryLevel, charging: item.charging, batteryAt: item.at });
                        }
                        // So did how the app is running (open, or minimised).
                        if (item.appState) {
                            applyTechDeviceState(item.techId, { appState: item.appState, appStateAt: item.at });
                        }
                        apply(item.techId, item.position, item.at);
                    });
                });

                // The one-at-a-time form, still sent by anything that has not been batched.
                skt.on("TechMapLocationUpdate", (techId: string, pos: HereGeocodePosition, at?: string) => {
                    apply(techId, pos, at);
                });
            },
            skt => {
                skt.off("TechMapLocationsUpdate");
                skt.off("TechMapLocationUpdate");
            }
        );

        // Clocking in and out is an event of its own, heard straight away: the ring turns on
        // the moment they clock in and the face goes grey the moment they clock out.
        watchTechClocks();

        const stopClock = onTechClock(techId => {
            refreshMarker(techId);

            const marker = techMarkersRef.current[techId];
            if (marker?.isPopupOpen?.() && marker.options?.tech) {
                marker.setPopupContent(popupFor(marker.options.tech));
            }
        });

        // A phone reporting what it allows changes the location line of an open panel.
        const stopDeviceState = onTechDeviceState(techId => {
            const marker = techMarkersRef.current[techId];
            if (marker?.isPopupOpen?.() && marker.options?.tech) {
                marker.setPopupContent(popupFor(marker.options.tech));
            }
        });

        // Going quiet is not an event — it is the absence of one — so it is noticed on a slow
        // sweep of the few technicians currently wearing the ring. Everyone else is untouched.
        const sweep = window.setInterval(() => {
            Array.from(liveMarkersRef.current).forEach(refreshMarker);

            // An open panel is plain markup, built once: rebuilt here so "Just now" turns into
            // "1 minute ago" while it is being read.
            Object.values(techMarkersRef.current).forEach((marker: any) => {
                if (marker?.isPopupOpen?.() && marker.options?.tech) {
                    marker.setPopupContent(popupFor(marker.options.tech));
                }
            });
        }, 20000);

        return () => {
            window.clearInterval(sweep);
            stopClock();
            stopDeviceState();
            socket.stop(key);
        };
    }, []);

    return (
        <Layout wide fitToolbar>
            <Section fluid fit>
                <div id="jobs-map-view" style={{ width: "100%", height: screen.heightDoc }} />
            </Section>
        </Layout>
    );
}
